import { cn } from '@today/ui-primitives'
import { type ReactElement, useCallback, useId, useState } from 'react'

/**
 * `localStorage` key holding the sidebar's collapsed state. The exact string
 * `'true'` means collapsed; any other value or an absent key means expanded.
 */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'today-sidebar-collapsed'

/**
 * The mockup-era name TO-633 shipped the key under (TO-636 renamed it). Read once
 * to carry a viewer's existing preference over, then deleted; never written.
 */
const LEGACY_SIDEBAR_COLLAPSED_STORAGE_KEY = 'mockup-v2-sidebar-collapsed'

/**
 * A viewer-permission predicate: given the viewer's atoms, is this nav item
 * visible? Registered in {@link AppSidebarProps.predicates} under a closed-set
 * name. A nav item names its predicate through {@link AppSidebarItem.visibleIf}.
 */
export type NavigationPredicate = (permissions: readonly string[]) => boolean

/** Raised when a nav item names a predicate the sidebar has no evaluator for. */
export class UnknownNavigationPredicateError extends Error {
	constructor(predicate: string) {
		super(`Unknown navigation predicate: "${predicate}". Register it in AppSidebar's predicates map.`)
		this.name = 'UnknownNavigationPredicateError'
	}
}

/** One navigation link. */
export interface AppSidebarItem {
	/** Visible link text. */
	label: string
	/** Link target; the item is marked current when it equals `currentPath`. */
	target: string
	/**
	 * Name of a closed-set predicate in {@link AppSidebarProps.predicates}. When
	 * present the item shows only if the predicate holds for the viewer's atoms.
	 * An item with no predicate is always shown.
	 */
	visibleIf?: string
	/**
	 * Optional short status chip (e.g. `'WIP'`) a consumer's {@link AppSidebarProps.renderLink}
	 * may surface beside the label — for a nav entry whose destination is not yet
	 * built. Pure data: the default renderer ignores it, so an item with no `badge`
	 * is unaffected; the chip's presentation and accessible-name composition are the
	 * consumer's `renderLink` concern (TO-635 TenantAdminShell). Kept on the shared
	 * item type so every plane's nav config is strongly typed.
	 */
	badge?: string
}

/** One navigation group: an optional header label and its items. */
export interface AppSidebarGroup {
	/** Stable group key. */
	key: string
	/** Section header; omitted groups render their items with no header. */
	label?: string
	items: AppSidebarItem[]
}

/**
 * Props for {@link AppSidebar}.
 *
 * @property groups - The per-plane nav config, rendered in order.
 * @property permissions - The viewer's permission atoms (ADR-0063). Items whose
 *   predicate is unsatisfied are absent from the DOM.
 * @property predicates - Closed-set name → evaluator map. An item naming a
 *   predicate not in this map raises {@link UnknownNavigationPredicateError}.
 * @property currentPath - The active path; the item whose `target` equals it is
 *   marked `aria-current="page"`.
 * @property label - Accessible name of the `<nav>` landmark.
 * @property renderLink - Renders one item as a link (the consumer wires routing);
 *   defaults to a plain `<a href>`.
 */
export interface AppSidebarProps {
	groups: AppSidebarGroup[]
	permissions: readonly string[]
	predicates?: Record<string, NavigationPredicate>
	currentPath: string
	label: string
	renderLink?: (item: AppSidebarItem, props: SidebarLinkProps) => ReactElement
}

/** Props the sidebar hands a custom link renderer. */
export interface SidebarLinkProps {
	target: string
	isCurrent: boolean
	className: string
	children: string
	'aria-current': 'page' | undefined
}

/**
 * Reads the stored collapsed state, migrating a legacy-key value to the current
 * key on the way (the current key wins when both exist) and removing the legacy
 * key. Runs before first paint, so a previously-collapsed viewer never sees the
 * sidebar flash open.
 */
function readInitialCollapsed(): boolean {
	try {
		const storage = globalThis.localStorage
		if (!storage) return false
		let stored = storage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
		if (stored === null) {
			stored = storage.getItem(LEGACY_SIDEBAR_COLLAPSED_STORAGE_KEY)
			if (stored !== null) storage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, stored)
		}
		storage.removeItem(LEGACY_SIDEBAR_COLLAPSED_STORAGE_KEY)
		return stored === 'true'
	} catch {
		// Storage disabled, or any read/write/remove failed: default to expanded, never throw.
		return false
	}
}

function persistCollapsed(collapsed: boolean): void {
	try {
		globalThis.localStorage?.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed))
	} catch {
		// Best-effort: a failed write must not break the toggle.
	}
}

function isItemVisible(
	item: AppSidebarItem,
	permissions: readonly string[],
	predicates: Record<string, NavigationPredicate>,
): boolean {
	if (!item.visibleIf) return true
	const evaluator = predicates[item.visibleIf]
	if (!evaluator) throw new UnknownNavigationPredicateError(item.visibleIf)
	return evaluator(permissions)
}

const LINK_BASE = 'block rounded-ds-md px-3 py-1.5 text-ds-sm'
const LINK_CURRENT = 'bg-ds-primary text-ds-on-primary font-medium'
const LINK_OTHER = 'text-ds-text-muted hover:bg-ds-surface-muted hover:text-ds-text'

function defaultRenderLink(_item: AppSidebarItem, props: SidebarLinkProps): ReactElement {
	return (
		<a href={props.target} aria-current={props['aria-current']} className={props.className}>
			{props.children}
		</a>
	)
}

interface SidebarGroupProps {
	group: AppSidebarGroup
	permissions: readonly string[]
	predicates: Record<string, NavigationPredicate>
	currentPath: string
	renderLink: (item: AppSidebarItem, props: SidebarLinkProps) => ReactElement
}

/**
 * Renders one nav group: an optional header and its permitted items. A group
 * whose every item is filtered out renders nothing — otherwise a gated group's
 * header (e.g. "Admin", "Platform") would leak the existence of features the
 * viewer's atoms do not permit (the REQ-002 absence invariant).
 */
function SidebarGroup({
	group,
	permissions,
	predicates,
	currentPath,
	renderLink,
}: SidebarGroupProps): ReactElement | null {
	const visibleItems = group.items.filter((item) => isItemVisible(item, permissions, predicates))
	if (visibleItems.length === 0) return null
	return (
		<div>
			{group.label ? (
				<div className="mb-ds-1 px-3 text-ds-xs font-semibold uppercase tracking-wider text-ds-text-subtle">
					{group.label}
				</div>
			) : null}
			<ul className="flex flex-col gap-ds-1">
				{visibleItems.map((item) => {
					const isCurrent = item.target === currentPath
					return (
						<li key={item.target}>
							{renderLink(item, {
								target: item.target,
								isCurrent,
								className: cn(LINK_BASE, isCurrent ? LINK_CURRENT : LINK_OTHER),
								children: item.label,
								'aria-current': isCurrent ? 'page' : undefined,
							})}
						</li>
					)
				})}
			</ul>
		</div>
	)
}

/**
 * Permission-aware, collapsible plane sidebar.
 *
 * @remarks
 * - **Permission-aware:** an item whose `visibleIf` predicate is unsatisfied is
 *   absent from the DOM, never CSS-hidden (defense in depth; the backend remains
 *   authoritative). ADR-0063: gating is on the permission atom, never a role.
 * - **Routing-free:** the active item is `item.target === currentPath`; the
 *   consumer supplies `renderLink` to wire its router (e.g. a `NavLink`).
 * - **Collapse persists** to `localStorage[SIDEBAR_COLLAPSED_STORAGE_KEY]`, and
 *   degrades to expanded (never throws) when storage is unavailable.
 */
export function AppSidebar({
	groups,
	permissions,
	predicates = {},
	currentPath,
	label,
	renderLink = defaultRenderLink,
}: AppSidebarProps): ReactElement {
	const [collapsed, setCollapsed] = useState<boolean>(readInitialCollapsed)
	const navId = useId()

	const toggleCollapsed = useCallback(() => {
		setCollapsed((previous) => {
			const next = !previous
			persistCollapsed(next)
			return next
		})
	}, [])

	return (
		<div className={cn('flex flex-col gap-ds-2', collapsed && 'items-center')}>
			<button
				type="button"
				onClick={toggleCollapsed}
				aria-expanded={!collapsed}
				aria-controls={navId}
				aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
				className="self-end rounded-ds-md px-2 py-1 text-ds-sm text-ds-text-muted hover:bg-ds-surface-muted hover:text-ds-text"
			>
				{collapsed ? '»' : '«'}
			</button>
			<nav id={navId} aria-label={label} hidden={collapsed} className="flex flex-col gap-ds-4">
				{groups.map((group) => (
					<SidebarGroup
						key={group.key}
						group={group}
						permissions={permissions}
						predicates={predicates}
						currentPath={currentPath}
						renderLink={renderLink}
					/>
				))}
			</nav>
		</div>
	)
}
